
import React from "react";
import { i18nManager } from "../../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, ITournamentEdition, ITournamentEditionAnalytics, ITournamentTransaction, SanitizedResponse, TournamentService, TransactionStatus } from "../../../../../../services";
import { Dropdown, FormControl, DateTimeInput, DateTimePickerLayout, DateHelper, DateTimePickerSelectionMode, ChartType, ChartData } from "@rivtn/noseur";
import { Utils, Schemes } from "../../../../../../utils";
import AnalCard from "../../../../../shared/analytics/AnalCard";
import { ChartManager } from "../../../../../shared/ChartManager";
import RecentTransactions from "../shared/RecentTransactions";

interface TournamentAnalyticsProps {
    tournamentEdition: ITournamentEdition;
}

function TournamentAnalytics(props: TournamentAnalyticsProps) {

    const { tournamentEdition } = props;
    const tournamentService = TournamentService.getInstance();
    const labels = i18nManager.Labels.host.manage_tournament.analytics;
    const tournamentAnalyticsChartDataState = React.useState<ChartData>();
    const [daysDifference, setDaysDifference] = React.useState<number>(0);
    const [tournamentAnalytics, setTournamentAnalytics] = React.useState<ITournamentEditionAnalytics>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [recentTransactions, setRecentTransactions] = React.useState<ApiResponseData<ITournamentTransaction>>({});
    const currencySign = i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN");

    React.useEffect(fetchInformation, []);

    React.useEffect(() => {
        setDaysDifference(DateHelper.getDaysBetweenDates(
            (searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()),
            (searchParams.has("to") ? new Date(searchParams.get("to")!) : new Date())));
    }, [searchParams]);

    return (<div className="earnings analytics">
        <div className="header">
            <div className="queries">
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ style: { marginLeft: 0 }, className: "plan-dd", isFieldset: true, label: i18nManager.Labels.teams.analytics.country_currency, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr") ?? "NGN")) ?? 0)}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={i18nManager.CountriesAndCurrencies} optionMap={{
                        value: "currency_code",
                        label: "{label} ({currency_name} - {currency_code})",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("curr", option.currency_code, setSearchParams, fetchInformation);
                    }} />
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={i18nManager.Labels.teams.analytics.date_range} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformation);
                        }} />
                </FormControl>
            </div>
            <div className="l" style={{ display: "flex", flexDirection: "column" }}>
                <span className="link lined-limit">{labels.export}</span>
            </div>
        </div>
        <div className="container" style={{ marginTop: 10 }}>
            <div className="primary" style={{ marginLeft: 0 }}>
                <div className="anal-cards" style={{ marginTop: 15 }}>
                    <AnalCard anal={tournamentAnalytics?.earnings?.total} currencySign={currencySign} desc={labels.earned_within_x_days} title={labels.total_earning}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} to={{ pathname: `./earnings`, search: window.location.search }} />
                    <AnalCard anal={tournamentAnalytics?.participants?.total} desc={labels.participants_registered} title={labels.participants}
                        icon="fa fa-users" analKey="count" daysDifference={daysDifference} to={{ pathname: `./participants`, search: window.location.search }} cardColor={"#EBFEF6"} />
                    <AnalCard anal={tournamentAnalytics?.matches?.total} desc={labels.matches_played} title={labels.matches}
                        icon="fa fa-soccer-ball" analKey="count" daysDifference={daysDifference} to={{ pathname: `./matches`, search: window.location.search }} cardColor={"#EBF4FE"} />
                    <AnalCard anal={tournamentAnalytics?.prize_pool?.total} desc={labels.prize_pool_distributed} title={labels.prize_pool}
                        icon="fa fa-trophy" analKey="count" daysDifference={daysDifference} to={{ pathname: `./prize-pool`, search: window.location.search }} cardColor={"#FEFBEB"} />
                </div>
                {<ChartManager type={ChartType.BAR} title={labels.tournament_anal} chartDataState={tournamentAnalyticsChartDataState} />}
                <RecentTransactions tournamentEdition={tournamentEdition} />
            </div>
        </div>
    </div>);

    function fetchInformation() {
        const params = {
            currency_code: searchParams.get("curr") ?? "NGN",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            fields: ["earnings.total", "participants.total", "matches.total", "prize_pool.total"],
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        };
        setTournamentAnalytics(undefined);
        tournamentService.getTournamentAnalytics(tournamentEdition.external_id, params).then(({ sanitized }: SanitizedResponse<ITournamentEditionAnalytics>) => {
            setTournamentAnalytics(sanitized);
        }).catch(BaseService.reportError);

        tournamentAnalyticsChartDataState[1](undefined);
        tournamentService.queryAnalyticsChartData(tournamentEdition.external_id, params).then(({ apiResponse }: SanitizedResponse<any>) => {
            tournamentAnalyticsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default TournamentAnalytics;
